import { AttachInternals, Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/init-services';
import { renderInputNativeInputChild } from '../../shared/form.util';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { getRandomId } from '../../utils/get-random-id';
import { querySelectorAllAsArray } from '../../utils/query-selector-all-as-array';
import { InputElement, InputElementType, InputMode, InputType } from './input.types';
import { CLICK_THROUGH_MODIFIER_CLASS, DEFAULT_INPUT_ELEMENT_TYPE, DEFAULT_INPUT_TYPE, ICON_FOR_CALENDAR, ICON_FOR_NOT_VISIBLE_PASSWORD, ICON_FOR_VISIBLE_PASSWORD, INPUT_DATE_TYPE, INPUT_PASSWORD_TYPE, SPECIAL_ACTION_CLASS } from './input.constants';
import { GreyBoxSize } from '../grey-box/grey-box.types';

/**
 * @slot label - Text placed within the inner label element (required)
 * @slot leading-icon - Icon to display at the start of the input (optional)
 * @slot actions - One or more wb-input-action elements that are placed at the end of the input (optional)
 * @slot message - Used to place a wb-control-hint or wb-control-error below the input (optional)
 */
@Component({
  tag: 'wb-input',
  styleUrl: 'input.scss',
  shadow: true,
  formAssociated: true,
})
export class Input implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** The size of the input */
  @Prop({ reflect: true })
  public size?: GreyBoxSize = 'l';

  /** Marks input as successful */
  @Prop({ reflect: true })
  public success?: boolean;

  /** Marks control as invalid. Automatically set as invalid if wb-control-error is provided. */
  @Prop({ reflect: true, mutable: true })
  public error?: boolean;

  /** The type of the input. */
  @Prop({ reflect: true })
  public type?: InputType = DEFAULT_INPUT_TYPE;

  /** This allows a browser to display an appropriate virtual keyboard. */
  @Prop({ reflect: true })
  public inputmode?: InputMode;

  /** Placeholder of the input. */
  @Prop({ reflect: true })
  public placeholder?: string;

  /** label describing action of icon that user can click */
  @Prop({ reflect: true })
  public specialActionLabel?: string = '';

  /** label describing to a screenreader the state of a validation */
  @Prop({ reflect: true })
  public readerValidationMessage?: string = '';

  /**
   * Makes the input component disabled.
   * This prevents users from being able to interact with the component, and conveys its inactive state to assistive technologies.
   */
  @Prop({ reflect: true })
  public disabled?: boolean = false;

  /** The name of the input component. */
  @Prop({ reflect: true })
  public name: string;

  /** The value of the input component. */
  @Prop({ reflect: true, mutable: true })
  public value?: string;

  /**
   * Determines whether the input is required or not.
   * An input marked as required will be announced as such to users of assistive technology.
   * When using this Prop you need to also set “novalidate” attribute on a form element to prevent browser from displaying its own validation errors.
   */
  @Prop({ reflect: true })
  public required?: boolean = false;

  /** Determines whether all the text is selected on focus or not. */
  @Prop({ reflect: true })
  public selectText?: boolean = false;

  /** Prevents the value from being editable. */
  @Prop({ reflect: true })
  public readonly?: boolean = false;

  /** If present, means the user can enter comma separated email addresses in the email input */
  @Prop({ reflect: true })
  public multiple?: boolean = false;

  /** If present, indicates that the input should automatically have focus when the page has finished loading */
  @Prop({ reflect: true, attribute: 'autofocus' })
  public autoFocus?: boolean = false; // stencil didn't like when the property had the name of `autofocus`

  /** Enables autocompletion on the input */
  @Prop({ reflect: true, attribute: 'autocomplete' })
  public autoComplete?: string; // stencil didn't like when the property had the name of `autocomplete`

  /** Defines the minimum number of charactersthe user can enter into the input field. */
  @Prop({ reflect: true })
  public minlength?: number;

  /** Defines the maximum number of charactersthe user can enter into the input field. */
  @Prop({ reflect: true })
  public maxlength?: number;

  /** The minimum value. */
  @Prop({ reflect: true })
  public min?: number | string;

  /** The maximum value. */
  @Prop({ reflect: true })
  public max?: number | string;

  /** In which increments the value should increase. */
  @Prop({ reflect: true })
  public step?: number;

  /** The pattern attribute, when specified, is a regular expression that the input's value must match in order for the value to pass constraint validation. */
  @Prop({ reflect: true })
  public pattern?: string;

  /** Determins wheter the component renders an input or a textarea element. */
  @Prop({ reflect: true })
  public as?: InputElementType = DEFAULT_INPUT_ELEMENT_TYPE;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public hasCaretIcon: boolean = false;

  /**
   * If present sets the direction of the element to rtl
   * @internal
   */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** Sets state as open for visual changes */
  @Prop({ reflect: true, mutable: true })
  public open: boolean = false;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public active: boolean = false;

  /** @internal marks the input as valid. When invalid, label should stay floated up to reveal entered value */
  @Prop({ reflect: true, mutable: true })
  public valid: boolean;

  /** Enables opening the native datepicker on click */
  @Prop()
  public openNativeDatepickerOnClick?: boolean = true;

  /** Displays compact variant */
  @Prop({ reflect: true })
  public compact: boolean = false;

  /** Defines if the element should grow relative to it's contents (textarea only) */
  @Prop({ reflect: true })
  public autoGrow: boolean = false;

  @State()
  public isPasswordVisible: boolean = false;

  @State()
  public hasLeadingIcon: boolean = false;

  @State()
  public labelText: string = '';

  /** Emitted when the input value changes */
  @Event()
  public wbchange: EventEmitter<{ name: string; value: any }>;

  /** Emitted when the input value changes */
  @Event({ composed: false })
  public change: EventEmitter<void>;

  /** Emitted when the input fires an input event */
  @Event()
  public wbinput: EventEmitter<{ name: string; value: any }>;

  /** Emitted when the input gets clicked */
  @Event()
  public wbinputclick: EventEmitter<object>;

  /** Emitted when the input gets focus */
  @Event()
  public wbfocus: EventEmitter<object>;

  /** Emitted when the input blurs */
  @Event()
  public wbblur: EventEmitter<void>;

  /** Emitted when the date special action is clicked */
  @Event()
  public wbdateaction: EventEmitter<void>;

  /** Emitted when the password special action is clicked */
  @Event()
  public wbpasswordaction: EventEmitter<void>;

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  /** Returns a reference to the currently rendered input or textarea element */
  @Method()
  public async getInputReference(): Promise<HTMLTextAreaElement | HTMLInputElement> {
    return this.inputEl;
  }

  /** Force a wbchange event (useful when programmatically changing the value) */
  @Method()
  public async emitWbchangeEvent(): Promise<void> {
    this.wbchange.emit({ name: this.name, value: this.value });
  }

  /** Force a wbinput event (useful when programmatically changing the value) */
  @Method()
  public async emitWbinputEvent(): Promise<void> {
    this.wbinput.emit({ name: this.name, value: this.value });
  }

  /** To allow the input to be used as a native date input https://stenciljs.com/docs/form-associated */
  @AttachInternals() internals: ElementInternals;

  /** The input validity state. Enables use of form association and constrain validation to consumers https://stenciljs.com/docs/form-associated https://developer.mozilla.org/en-US/docs/Web/HTML/Constraint_validation */
  @Prop({ mutable: true })
  public validity: ValidityState;

  /** The input validity message. Enables use of form association and constrain validation to consumers https://stenciljs.com/docs/form-associated https://developer.mozilla.org/en-US/docs/Web/HTML/Constraint_validation */
  @Prop({ mutable: true })
  public validationMessage: string;

  /** If true the input is a candidate for constraint validation. Enables use of form association and constrain validation to consumers https://stenciljs.com/docs/form-associated https://developer.mozilla.org/en-US/docs/Web/HTML/Constraint_validation */
  @Prop({ mutable: true })
  public willValidate: boolean;

  /** Set form association values to allow consumers to use of form association and constrain validation to consumers https://stenciljs.com/docs/form-associated https://developer.mozilla.org/en-US/docs/Web/HTML/Constraint_validation */
  @Watch('value')
  public onValueChange(newValue: string) {
    this.internals.setFormValue(newValue);

    if (!this.inputEl) {
      return;
    }

    if (newValue) {
      this.inputEl.value = newValue;
    }

    this.internals.setValidity(this.inputEl.validity, this.inputEl?.validationMessage, this.inputEl);

    this.validity = this.inputEl.validity;
    this.validationMessage = this.inputEl.validationMessage;
    this.willValidate = this.inputEl.willValidate;
  }

  formAssociatedCallback() {
    if (!this.inputEl) {
      return;
    }

    this.validity = this.inputEl.validity;
    this.validationMessage = this.inputEl.validationMessage;
    this.willValidate = this.inputEl.willValidate;
  }

  private TagNames = transformTagNames(this, 'wb-input', ['wb-round-button', 'wb-grey-box', 'wb-icon', 'wb-text', 'wb-control-error', 'wb-control-hint', 'wb-input-action', 'wb-visually-hidden']);

  private inputEl: InputElement | undefined;
  private randomInputId: string;

  public connectedCallback(): void {
    this.randomInputId = `input-${getRandomId()}`;
    this.updateDirection();
  }

  public componentDidLoad(): void {
    this.active = Boolean(document.activeElement === this.inputEl);
    this.onValueChange(this.value);
    this.validateInput();

    // Forcing autofocus working on Firefox and Safari
    if (this.autoFocus) {
      this.inputEl.focus();
    }
  }

  private handleLabelChange = (): void => {
    const labelSlot = this.el.shadowRoot.querySelector('slot[name="label"]') as HTMLSlotElement;
    if (labelSlot) {
      const assignedNodes = labelSlot.assignedNodes({ flatten: true });
      this.labelText = assignedNodes
        .map((node) => node.textContent)
        .join('')
        .trim();
    }
  };

  public handleLeadingIconChange = (): void => {
    this.hasLeadingIcon = Boolean(this.el.querySelector('[slot="leading-icon"]'));
  };

  private handleClick = (): void => {
    this.active = true;
    this.wbinputclick.emit({ isNativeInput: this.active });
  };

  private handleFocus = (): void => {
    this.active = true;
    this.wbfocus.emit({ isNativeInput: this.active });

    // Selecting all text if prop is truthy
    if (this.selectText) {
      this.inputEl.select();
    }
  };

  private handleBlur = (): void => {
    this.active = false;
    this.autoFocus = null;
    this.wbblur.emit();
  };

  private handleInput = (evt: Event): void => {
    const target = evt.target as InputElement;
    this.value = target.value;
    this.validateInput();
    this.emitWbinputEvent();
  };

  private handleChange = (event: Event): void => {
    if (!event.composed) {
      this.change.emit();
    }
    this.emitWbchangeEvent();
  };

  private validateInput = (): void => {
    this.valid = this.inputEl.validity.valid;
  };

  private handlePasswordActionClick = () => {
    this.togglePasswordVisibility();
    this.wbpasswordaction.emit();
  };

  private handleDateActionClick = () => {
    this.wbdateaction.emit();
  };

  private togglePasswordVisibility = (): void => {
    this.isPasswordVisible = !this.isPasswordVisible;
    this.inputEl.setAttribute('type', this.isPasswordVisible ? DEFAULT_INPUT_TYPE : INPUT_PASSWORD_TYPE);
  };

  private setInputReference = (ref): void => {
    this.inputEl = ref;
  };

  private hasError(): string {
    return this.el.querySelector(`${this.TagNames.WbControlError}[slot="message"]`) != null ? 'true' : 'false';
  }

  private handleMessageSlotChange = (): void => {
    const messageEl = this.el.querySelector<HTMLWbControlErrorElement | HTMLWbControlHintElement>(`${this.TagNames.WbControlError},${this.TagNames.WbControlHint}`);

    if (messageEl) {
      const message = messageEl.textContent;
      const messageContainer = document.createElement(this.TagNames.WbVisuallyHidden);
      messageContainer.textContent = message;
      messageContainer.id = messageContainer.id || `message-${getRandomId()}`;
      this.inputEl.setAttribute('aria-describedby', messageContainer.id);
      this.inputEl.appendChild(messageContainer);
    } else {
      this.inputEl.removeAttribute('aria-describedby');
    }

    this.error = Boolean(this.el.querySelector(this.TagNames.WbControlError));
  };

  private updateInputActionsWidth = (): void => {
    const widthPx = querySelectorAllAsArray(this.el, this.TagNames.WbInputAction).reduce((acc, el) => {
      const computedStyle = getComputedStyle(el);

      return acc + el.clientWidth + parseInt(computedStyle.marginInlineStart) + parseInt(computedStyle.borderInlineStartWidth);
    }, 0);
    this.el.style.setProperty('--input-actions-width', `${Number.isNaN(widthPx) ? 0 : widthPx}px`);
  };

  private get hasFloatingLabel(): boolean {
    const hasInvalidValue = this.inputEl && !this.inputEl.validity.valid && !this.inputEl.validity.valueMissing;
    return hasInvalidValue || this.active || Boolean(this.value);
  }

  public render(): JSX.Element {
    renderInputNativeInputChild(this.el, { type: 'hidden', name: this.name, value: this.value || '' });
    const { WbGreyBox } = this.TagNames;

    const SpecialAction = (): JSX.Element => {
      const { WbIcon, WbRoundButton } = this.TagNames;

      //  TODO: fixing Datepicker when showPicker API is available
      //  https://caniuse.com/?search=showPicker
      //  https://git.i.mercedes-benz.com/dh-io-workbench/core/issues/2813

      if (this.type === INPUT_DATE_TYPE) {
        return (
          <WbRoundButton
            class={{
              [SPECIAL_ACTION_CLASS]: true,
              [CLICK_THROUGH_MODIFIER_CLASS]: this.openNativeDatepickerOnClick || this.disabled,
            }}
            size={`new-${this.size}`}
            onClick={this.handleDateActionClick}
            button-aria-label={this.specialActionLabel}
          >
            <WbIcon name={ICON_FOR_CALENDAR} />
          </WbRoundButton>
        );
      } else if (this.type === INPUT_PASSWORD_TYPE) {
        return (
          <WbRoundButton
            class={{
              [SPECIAL_ACTION_CLASS]: true,
              [CLICK_THROUGH_MODIFIER_CLASS]: this.disabled,
            }}
            size={`new-${this.size}`}
            onClick={this.handlePasswordActionClick}
            button-aria-label={this.specialActionLabel}
          >
            <WbIcon name={this.isPasswordVisible ? ICON_FOR_VISIBLE_PASSWORD : ICON_FOR_NOT_VISIBLE_PASSWORD} />
          </WbRoundButton>
        );
      }
    };

    const TextArea = (): JSX.Element => (
      <textarea
        id={this.randomInputId}
        aria-invalid={this.hasError()} //
        autoFocus={this.autoFocus}
        disabled={this.disabled}
        inputmode={this.inputmode}
        maxLength={this.maxlength}
        minLength={this.minlength}
        name={this.name}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onClick={this.handleClick}
        onFocus={this.handleFocus}
        onInput={this.handleInput}
        placeholder={this.placeholder}
        readonly={this.readonly}
        ref={this.setInputReference}
        required={this.required}
        value={this.value}
      />
    );

    const Input = (): JSX.Element => {
      if (this.as === 'textarea') {
        if (this.autoGrow) {
          return (
            <div class="textarea-wrap" data-replicated-value={this.value}>
              <TextArea />
            </div>
          );
        }
        return <TextArea />;
      } else {
        return (
          <input
            id={this.randomInputId}
            aria-invalid={this.hasError()} //
            autoComplete={this.autoComplete}
            autoFocus={this.autoFocus}
            disabled={this.disabled}
            inputmode={this.inputmode}
            max={this.max}
            maxLength={this.maxlength}
            min={this.min}
            minLength={this.minlength}
            name={this.name}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            onClick={this.handleClick}
            onFocus={this.handleFocus}
            onInput={this.handleInput}
            pattern={this.pattern}
            placeholder={this.placeholder}
            readonly={this.readonly}
            ref={this.setInputReference}
            required={this.required}
            step={this.step}
            type={this.type}
            value={this.value}
          />
        );
      }
    };

    const Label = (): JSX.Element => {
      const hasAsterisk = this.labelText.endsWith('*');

      return (
        <label htmlFor={this.randomInputId} class="label">
          <slot name="label" onSlotchange={this.handleLabelChange} />
          {this.required && !hasAsterisk && ' *'}
        </label>
      );
    };

    return (
      <Host class="wb-input" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <WbGreyBox
          size={this.as === 'textarea' ? 'l' : this.size}
          active={this.active}
          error={this.error}
          success={this.success}
          autofocus={this.autoFocus}
          disabled={this.disabled}
          readonly={this.readonly}
          hasCaretIcon={this.hasCaretIcon}
          hasStatusIcons
          open={this.open}
          readerValidationMessage={this.readerValidationMessage}
          buttonActionLabel={this.specialActionLabel}
          class={{
            'has-leading-icon': this.hasLeadingIcon,
            'has-floating-label': this.hasFloatingLabel,
          }}
        >
          <slot name="leading-icon" onSlotchange={this.handleLeadingIconChange} />
          <Label />
          <Input />
          <slot name="actions" onSlotchange={this.updateInputActionsWidth} />
          <SpecialAction />
        </WbGreyBox>
        <div class="message-container" aria-live="polite">
          <slot name="message" onSlotchange={this.handleMessageSlotChange} />
        </div>
      </Host>
    );
  }
}
